// ────────────────────────────────────────────────────────────────────
// The three panes that render a file: the Details list, the Tiles grid, the
// Preview drawer — plus the right-click menu that offers the same verbs.
//
// These four agree on the actions a file affords (open, file, rename, import,
// delete), so they live together: an action added to the menu and forgotten in
// the preview is the kind of half-wired seam this codebase keeps producing.
// ────────────────────────────────────────────────────────────────────

import { FolderInput, FolderOutput, Trash2, ExternalLink, Download, Layers,
         RotateCw, Pencil, Tag, CalendarDays, Database, X } from "lucide-react";
import { fmtSize, fmtWhen, typeLabel, openFile, canImportFile, canRegisterImportFile,
         type SortKey, type Menu } from "@/lib/intakeFiles";
import { KindIcon, CheckBox, Hd, Center } from "@/components/intake/primitives";
import type { IntakeFile } from "@/lib/types";

// ── Details (list) view ──
export function DetailsView({ files, pending, emptyText, selected, sort, setSort, onClick, onMenu, onToggle, allSel, someSel, onToggleAll, onDragStart, onDragEnd }: {
  files: IntakeFile[]; pending: boolean; emptyText: string; selected: (id: number) => boolean;
  sort: { key: SortKey; asc: boolean }; setSort: (s: { key: SortKey; asc: boolean }) => void;
  onClick: (e: React.MouseEvent, f: IntakeFile) => void; onMenu: (e: React.MouseEvent, f: IntakeFile) => void;
  onToggle: (id: number) => void; allSel: boolean; someSel: boolean; onToggleAll: () => void;
  onDragStart?: (f: IntakeFile) => void; onDragEnd: () => void;
}): JSX.Element {
  return (
    <table className="w-full font-mono" style={{ fontSize: 12, borderCollapse: "collapse" }}>
      <thead className="sticky top-0 z-10" style={{ background: "var(--paper)" }}>
        <tr style={{ borderBottom: "1px solid var(--hairline)" }}>
          <th className="pl-6 pr-1 py-2" style={{ width: 30 }}>
            <CheckBox on={allSel} indeterminate={someSel && !allSel} onClick={onToggleAll} title="Select all" />
          </th>
          <Hd label="Name" k="name" sort={sort} setSort={setSort} />
          <Hd label="Date modified" k="date" sort={sort} setSort={setSort} />
          <Hd label="Type" k="type" sort={sort} setSort={setSort} />
          <Hd label="Size" k="size" sort={sort} setSort={setSort} right />
        </tr>
      </thead>
      <tbody>
        {pending && <tr><td colSpan={5} className="text-center py-10" style={{ color: "var(--ink-3)" }}>Loading…</td></tr>}
        {!pending && files.length === 0 && (
          <tr><td colSpan={5} className="text-center py-16" style={{ color: "var(--ink-3)" }}>
            {emptyText}
          </td></tr>
        )}
        {files.map((r) => {
          const isPhoto = r.kind === "photo" && !!r.download_url;
          return (
            <tr key={r.id} draggable={!!onDragStart} onDragStart={() => onDragStart?.(r)} onDragEnd={onDragEnd}
                onClick={(e) => onClick(e, r)} onDoubleClick={() => openFile(r)} onContextMenu={(e) => onMenu(e, r)}
                className="cursor-default transition-colors"
                style={{ borderBottom: "1px solid var(--hairline)", background: selected(r.id) ? "rgba(var(--accent-rgb) / 0.10)" : undefined }}>
              <td className="pl-6 pr-1 py-2 align-middle" onClick={(e) => e.stopPropagation()}>
                <CheckBox on={selected(r.id)} onClick={() => onToggle(r.id)} />
              </td>
              <td className="pl-1 pr-3 py-2 align-middle">
                <span className="flex items-center gap-2.5 min-w-0">
                  <span className="shrink-0 flex items-center justify-center overflow-hidden" style={{ width: 22, height: 22, background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
                    {isPhoto ? <img src={r.download_url!} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} draggable={false} /> : <KindIcon kind={r.kind} size={13} />}
                  </span>
                  <span className="truncate" style={{ color: "var(--ink-1)", maxWidth: 360 }}>{r.filename}</span>
                  {r.note && <span className="truncate" style={{ color: "var(--ink-4)", fontSize: 10 }}>· {r.note}</span>}
                </span>
              </td>
              <td className="px-3 py-2 tabular-nums align-middle" style={{ color: "var(--ink-3)" }}>{fmtWhen(r.filed_at ?? r.uploaded_at)}</td>
              <td className="px-3 py-2 align-middle" style={{ color: "var(--ink-3)" }}>{typeLabel(r)}</td>
              <td className="px-3 py-2 text-right tabular-nums align-middle" style={{ color: "var(--ink-2)" }}>{fmtSize(r.size_bytes)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Tiles (icon grid) view ──
export function TilesView({ files, pending, emptyText, selected, onClick, onMenu, onDragStart, onDragEnd }: {
  files: IntakeFile[]; pending: boolean; emptyText: string; selected: (id: number) => boolean;
  onClick: (e: React.MouseEvent, f: IntakeFile) => void; onMenu: (e: React.MouseEvent, f: IntakeFile) => void;
  onDragStart?: (f: IntakeFile) => void; onDragEnd: () => void;
}): JSX.Element {
  if (pending) return <Center>Loading…</Center>;
  if (files.length === 0) return <Center>{emptyText}</Center>;
  return (
    <div className="p-5 grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(128px,1fr))" }}>
      {files.map((r) => {
        const isPhoto = r.kind === "photo" && !!r.download_url;
        const sel = selected(r.id);
        return (
          <div key={r.id} draggable={!!onDragStart} onDragStart={() => onDragStart?.(r)} onDragEnd={onDragEnd}
               onClick={(e) => onClick(e, r)} onDoubleClick={() => openFile(r)} onContextMenu={(e) => onMenu(e, r)}
               className="flex flex-col cursor-default transition-colors" title={r.filename}
               style={{ border: `1px solid ${sel ? "var(--accent)" : "var(--hairline)"}`, background: sel ? "rgba(var(--accent-rgb) / 0.08)" : "var(--paper-2)" }}>
            <div className="flex items-center justify-center overflow-hidden" style={{ height: 92, background: "var(--paper)", borderBottom: "1px solid var(--hairline)" }}>
              {isPhoto ? <img src={r.download_url!} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} draggable={false} /> : <KindIcon kind={r.kind} size={30} />}
            </div>
            <div className="px-2 py-1.5">
              <div className="font-mono truncate" style={{ fontSize: 11, color: "var(--ink-1)" }}>{r.filename}</div>
              <div className="font-mono tabular-nums" style={{ fontSize: 9, color: "var(--ink-4)" }}>{fmtSize(r.size_bytes)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Preview drawer (slides in from the right when a file is selected) ──
export function PreviewPane({ file, show, isStaff, inTrash, navigate, onFile, onRename, onEditNote, onEditDate, onImport, onImportRegister, onDelete, onRestore, onPurge, onClose }: {
  file: IntakeFile | null; show: boolean; isStaff: boolean; inTrash: boolean; navigate: (to: string) => void;
  onFile: (f: IntakeFile) => void; onRename: (f: IntakeFile) => void; onEditNote: (f: IntakeFile) => void;
  onEditDate: (f: IntakeFile) => void;
  onImport: (f: IntakeFile) => void; onImportRegister: (f: IntakeFile) => void;
  onDelete: (f: IntakeFile) => void; onRestore: (f: IntakeFile) => void; onPurge: (f: IntakeFile) => void; onClose: () => void;
}): JSX.Element {
  const isPdf = !!file && file.download_url && (file.kind === "report") && file.filename.toLowerCase().endsWith(".pdf");
  const btn = "font-mono uppercase inline-flex items-center justify-center gap-1.5 px-3 py-1.5 border transition active:translate-y-px";
  return (
    <aside className="absolute top-0 right-0 h-full z-40 overflow-y-auto"
           style={{ width: 300, borderLeft: "1px solid var(--hairline)", background: "var(--paper-2)",
                    boxShadow: "-8px 0 20px rgba(10,15,24,0.08)",
                    transform: show && file ? "translateX(0)" : "translateX(100%)",
                    transition: "transform .22s ease", pointerEvents: show && file ? "auto" : "none" }}>
      {file && (
        <div>
          <div className="px-4 py-2.5 flex items-center justify-between border-b sticky top-0 z-10" style={{ borderColor: "var(--hairline)", background: "var(--paper-2)" }}>
            <span className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: ".18em", color: "var(--ink-4)" }}>Preview</span>
            <button onClick={onClose} title="Close" style={{ color: "var(--ink-3)" }}><X size={14} /></button>
          </div>
          <div className="p-4">
          <div className="overflow-hidden mb-3" style={{ height: isPdf ? 260 : 180, background: "var(--paper)", border: "1px solid var(--hairline)" }}>
            {file.kind === "photo" && file.download_url
              ? <img src={file.download_url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
              : isPdf
              ? <iframe src={`${file.download_url}#toolbar=0&view=FitH`} title="preview" style={{ width: "100%", height: "100%", border: "none" }} />
              : <div className="h-full flex items-center justify-center"><KindIcon kind={file.kind} size={54} /></div>}
          </div>
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="font-mono break-all" style={{ fontSize: 12.5, color: "var(--ink-1)", fontWeight: 600 }}>{file.filename}</div>
            {isStaff && !inTrash && <button onClick={() => onRename(file)} title="Rename" className="shrink-0 hover:opacity-70 mt-0.5" style={{ color: "var(--ink-3)" }}><Pencil size={13} /></button>}
          </div>
          <Meta label="Type" value={typeLabel(file)} />
          <Meta label="Size" value={fmtSize(file.size_bytes)} />
          <Meta label="Uploaded by" value={file.uploaded_by || "—"} />
          <Meta label="Uploaded" value={fmtWhen(file.uploaded_at)} />
          <div className="mb-1.5 flex items-start justify-between gap-2">
            <div>
              <div className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".18em", color: "var(--ink-4)" }}>Note</div>
              <div className="font-mono break-words" style={{ fontSize: 11.5, color: file.note ? "var(--ink-2)" : "var(--ink-3)" }}>{file.note || "—"}</div>
            </div>
            {isStaff && !inTrash && <button onClick={() => onEditNote(file)} title="Edit note" className="shrink-0 hover:opacity-70 mt-0.5" style={{ color: "var(--ink-3)" }}><Tag size={12} /></button>}
          </div>
          {/* The date printed ON the document, as distinct from "Uploaded"
              above it — §4.4. Shown even when empty (and it starts empty for
              every row: nothing infers it), because a blank field with an edit
              button next to it is how the reader learns the field exists and
              that filling it is their job. */}
          <div className="mb-1.5 flex items-start justify-between gap-2">
            <div>
              <div className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".18em", color: "var(--ink-4)" }}>วันที่บนเอกสาร</div>
              <div data-testid="preview-document-date" className="font-mono" style={{ fontSize: 11.5, color: file.document_date ? "var(--ink-2)" : "var(--ink-3)" }}>
                {file.document_date || "ยังไม่ระบุ"}
              </div>
            </div>
            {isStaff && !inTrash && (
              <button data-testid="preview-edit-document-date" onClick={() => onEditDate(file)} title="ระบุวันที่บนเอกสาร"
                      className="shrink-0 hover:opacity-70 mt-0.5" style={{ color: "var(--ink-4)" }}><CalendarDays size={12} /></button>
            )}
          </div>
          <Meta label="Status" value={inTrash ? "In Trash" : file.status === "filed" ? "Filed" : "In inbox"} />
          {file.status === "filed" && file.asset_tag && (
            <div className="mt-1">
              <div className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".18em", color: "var(--ink-4)" }}>Filed to</div>
              <button onClick={() => file.asset_id && navigate(`/assets/${file.asset_id}`)} className="font-mono hover:underline inline-flex items-center gap-1 mt-0.5"
                      style={{ fontSize: 12, color: "var(--ok)", fontWeight: 600 }}>{file.asset_tag} <ExternalLink size={10} /></button>
              {file.filed_by && <div className="font-mono" style={{ fontSize: 9.5, color: "var(--ink-4)" }}>by {file.filed_by} · {fmtWhen(file.filed_at)}</div>}
            </div>
          )}
          <div className="flex flex-col gap-2 mt-4">
            {file.download_url && (
              <a href={file.download_url} target="_blank" rel="noreferrer" className={`${btn} hover:bg-[rgba(31,78,121,0.05)]`}
                 style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--ink-2)", borderColor: "var(--hairline)" }}><Download size={12} /> Download</a>
            )}
            {isStaff && inTrash ? (
              <>
                <button onClick={() => onRestore(file)} className={`${btn} hover:bg-[rgba(var(--accent-rgb)_/_0.08)]`}
                        style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--accent-deep)", borderColor: "var(--accent)", fontWeight: 700 }}><RotateCw size={12} /> Restore</button>
                <button onClick={() => onPurge(file)} className={`${btn} hover:bg-[rgba(192,57,43,0.06)]`}
                        style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--err)", borderColor: "rgba(192,57,43,0.4)" }}><Trash2 size={12} /> Delete forever</button>
              </>
            ) : isStaff ? (
              <>
                {canImportFile(file) && (
                  <button onClick={() => onImport(file)} className={`${btn} hover:bg-[rgba(21,128,61,0.08)]`}
                          style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--ok)", borderColor: "rgba(21,128,61,0.5)", fontWeight: 700 }}>
                    <Database size={12} /> Import to asset</button>
                )}
                {canRegisterImportFile(file) && (
                  <button onClick={() => onImportRegister(file)} className={`${btn} hover:bg-[rgba(21,128,61,0.08)]`}
                          style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--ok)", borderColor: "rgba(21,128,61,0.5)", fontWeight: 700 }}>
                    <Layers size={12} /> Import as asset register</button>
                )}
                <button onClick={() => onFile(file)} className={`${btn} hover:bg-[rgba(var(--accent-rgb)_/_0.08)]`}
                        style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--accent-deep)", borderColor: "var(--accent)", fontWeight: 700 }}>
                  <FolderInput size={12} /> {file.asset_id ? "Move to asset" : "File to asset"}</button>
                <button onClick={() => onDelete(file)} className={`${btn} hover:bg-[rgba(192,57,43,0.06)]`}
                        style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--err)", borderColor: "rgba(192,57,43,0.4)" }}><Trash2 size={12} /> Delete</button>
              </>
            ) : null}
          </div>
          </div>
        </div>
      )}
    </aside>
  );
}
function Meta({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="mb-1.5">
      <div className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".18em", color: "var(--ink-4)" }}>{label}</div>
      <div className="font-mono break-words" style={{ fontSize: 11.5, color: "var(--ink-2)" }}>{value}</div>
    </div>
  );
}

// ── Context menu ──
export function ContextMenu({ menu, isStaff, inTrash, canImport, canImportRegister, navigate, onOpen, onFile, onUnfile, onRename, onEditNote, onEditDate, onImport, onImportRegister, onDelete, onRestore, onPurge, selCount }: {
  menu: NonNullable<Menu>; isStaff: boolean; inTrash: boolean; canImport: boolean; canImportRegister: boolean; navigate: (to: string) => void;
  onOpen: () => void; onFile: () => void; onUnfile: () => void; onRename: () => void; onEditNote: () => void;
  onEditDate: () => void;
  onImport: () => void; onImportRegister: () => void; onDelete: () => void; onRestore: () => void; onPurge: () => void; selCount: number;
}): JSX.Element {
  const f = menu.file;
  const many = selCount > 1;
  const x = Math.min(menu.x, window.innerWidth - 220);
  const y = Math.min(menu.y, window.innerHeight - 240);
  return (
    <div className="fixed z-[1300] w-[210px] shadow-2xl py-1" onClick={(e) => e.stopPropagation()}
         style={{ left: x, top: y, background: "var(--paper)", border: "1px solid var(--hairline)" }}>
      {!many && <MenuItem icon={<ExternalLink size={13} />} label="Open" disabled={!f.download_url} onClick={onOpen} />}
      {inTrash ? (
        <>
          {isStaff && <MenuItem icon={<RotateCw size={13} />} label={many ? `Restore ${selCount}` : "Restore"} onClick={onRestore} />}
          {isStaff && <Divider />}
          {isStaff && <MenuItem icon={<Trash2 size={13} />} label={many ? `Delete ${selCount} forever` : "Delete forever"} danger onClick={onPurge} />}
        </>
      ) : (
        <>
          {!many && f.asset_id && <MenuItem icon={<ExternalLink size={13} />} label="Open asset" onClick={() => navigate(`/assets/${f.asset_id}`)} />}
          {isStaff && <Divider />}
          {isStaff && <MenuItem icon={<FolderInput size={13} />} label={many ? `File ${selCount} to asset…` : f.asset_id ? "Move to asset…" : "File to asset…"} onClick={onFile} />}
          {isStaff && !many && f.asset_id && <MenuItem icon={<FolderOutput size={13} />} label="Move to Inbox" onClick={onUnfile} />}
          {isStaff && !many && canImport && <MenuItem icon={<Database size={13} />} label="Import to asset…" onClick={onImport} />}
          {isStaff && !many && canImportRegister && <MenuItem icon={<Layers size={13} />} label="Import as asset register…" onClick={onImportRegister} />}
          {isStaff && !many && <MenuItem icon={<Pencil size={13} />} label="Rename…" onClick={onRename} />}
          {isStaff && !many && <MenuItem icon={<Tag size={13} />} label="Edit note…" onClick={onEditNote} />}
          {/* Offered for one row and for many: the pile is the whole reason
              the column can ever be filled (§4.4). */}
          {isStaff && <MenuItem icon={<CalendarDays size={13} />} label={many ? `ระบุวันที่ ${selCount} ฉบับ…` : "ระบุวันที่บนเอกสาร…"} onClick={onEditDate} />}
          {isStaff && <Divider />}
          {isStaff && <MenuItem icon={<Trash2 size={13} />} label={many ? `Delete ${selCount}` : "Delete"} danger onClick={onDelete} />}
        </>
      )}
    </div>
  );
}
function MenuItem({ icon, label, onClick, disabled, danger }: { icon: JSX.Element; label: string; onClick: () => void; disabled?: boolean; danger?: boolean }): JSX.Element {
  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled}
            className="w-full flex items-center gap-2.5 px-3 py-1.5 text-left font-mono transition-colors disabled:opacity-40 hover:bg-[rgba(31,78,121,0.06)]"
            style={{ fontSize: 12, color: danger ? "var(--err)" : "var(--ink-2)" }}>
      <span style={{ color: danger ? "var(--err)" : "var(--ink-4)" }}>{icon}</span>{label}
    </button>
  );
}
function Divider(): JSX.Element { return <div className="my-1" style={{ borderTop: "1px solid var(--hairline)" }} />; }

